import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getHttpEndpoint } from '@orbs-network/ton-access';
import { Address, beginCell, Cell, fromNano, toNano, TonClient } from '@ton/ton';
import {
  Account,
  CHAIN,
  ITonConnect,
  SendTransactionRequest,
  SignDataPayload,
  THEME,
  TonConnectUI,
  WalletsModalState,
} from '@tonconnect/ui';
import {
  BehaviorSubject,
  defer,
  distinctUntilChanged,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  Observable,
  retry,
  timer,
} from 'rxjs';
import { AppService } from 'src/app/app.service';
import { environment } from 'src/environments/environment';
import { AccountApi } from '../../apis/account.api';
import { Web3WalletSignatureInfo, Web3WalletType } from '../../interfaces/auth.interface';
import { NetworksInterface } from '../../interfaces/deposit.interface';
import { Web3WalletAdapter } from '../../interfaces/web3-wallet.interface';
import { LocalStorageService } from '../localstorage.service';
import { ToastService } from '../toast.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TonConnectService implements Web3WalletAdapter, OnDestroy {
  constructor(
    private appService: AppService,
    private toastService: ToastService,
    private localStorageService: LocalStorageService,
    private accountApi: AccountApi,
  ) {
    // TonConnectUI 實例化與初始化（確保 TonConnect 的 UI 實例是單例 (Singleton) 模式）
    // Handle web component tag Name registration error when login/logout trigger window.reBuild.
    // (NotSupportedError: Failed to execute 'define' on 'CustomElementRegistry')
    if (!window.TonConnectUiInstance) {
      const host = environment.production ? window.location.origin : environment.apiUrl;
      const manifestUrl = `${host}/v1/member/auth/gettonconnectmanifest/${environment.common.tenant}`;
      window.TonConnectUiInstance = new TonConnectUI({ manifestUrl });
    }
    this._tonConnectUI = window.TonConnectUiInstance;

    // 同步 _tonConnectUI 的狀態
    this._isConnected$ = new BehaviorSubject(false);
    this._accounts$ = new BehaviorSubject(this._getPublicAddresses(this._tonConnectUI?.account));
    this._currentChainId$ = new BehaviorSubject<number | string | null>(this._tonConnectUI?.account?.chain || null);

    // 暴露為可觀察對象
    this.isConnected$ = this._isConnected$.asObservable();
    this.accounts$ = this._accounts$.asObservable();
    this.currentChainId$ = this._currentChainId$.asObservable();

    this._registerRequirementListeners();

    this.checkExistingConnection();
  }

  /** Ton Connect UI 實例 */
  public readonly _tonConnectUI: TonConnectUI;

  /** Mainnet TON RPC Client，用於與 TON 區塊鏈互動 */
  private _mainTonClient!: TonClient;

  /** TestNet TON RPC Client，用於與 TON 區塊鏈互動 */
  private _testTonClient!: TonClient;

  /** 快取 Jetton 子錢包地址 (避免重複的網路請求，提升反應速度和使用者體驗，並減少對 TON RPC 節點的負載) */
  private _jettonWalletAddressCache: Map<string, Address> = new Map();

  /** 移除「錢包狀態」事件監聽 */
  private _walletStatusListenerUnsubscribe: ReturnType<ITonConnect['onStatusChange']> | null = null;

  private readonly _isConnected$: BehaviorSubject<boolean>;
  private readonly _accounts$: BehaviorSubject<string[]>;
  private readonly _currentChainId$: BehaviorSubject<number | string | null>;

  readonly isConnected$: Observable<boolean>;
  readonly accounts$: Observable<string[]>;
  readonly currentChainId$: Observable<number | string | null>;

  ngOnDestroy(): void {
    if (this._walletStatusListenerUnsubscribe) {
      this._walletStatusListenerUnsubscribe();
      this._walletStatusListenerUnsubscribe = null;
    }
  }

  /**
   * 交易請求的有效時間
   *
   * @readonly
   * @private
   * @type {number}
   * @memberof TonConnectService
   */
  private get _transactionValidUntil(): number {
    /** 交易 Timeout 時限（單位：秒） */
    const TRANSACTION_VALID_UNTIL_DURATION_SECOND = 60;
    return Math.floor(Date.now() / 1000) + TRANSACTION_VALID_UNTIL_DURATION_SECOND;
  }

  /**
   * 依照傳入的 chain，確認 tonClient 的鏈別（主網/測試網）
   *
   * @private
   * @param {CHAIN} chain 鏈別
   * @returns {Promise<TonClient>} TonClient 實例
   * @memberof TonConnectService
   */
  private async _getTonClient(chain: CHAIN): Promise<TonClient> {
    switch (chain) {
      case CHAIN.MAINNET:
        if (!this._mainTonClient) {
          const endpoint = await getHttpEndpoint({ network: 'mainnet' });
          this._mainTonClient = new TonClient({ endpoint });
        }
        return this._mainTonClient;

      default:
        if (!this._testTonClient) {
          const endpoint = await getHttpEndpoint({ network: 'testnet' });
          this._testTonClient = new TonClient({ endpoint });
        }
        return this._testTonClient;
    }
  }

  /**
   * 取得 原始格式(Raw) 地址
   *
   * @description 工作鍊和十六進位編碼的雜湊值，以「:」字元分隔
   * @see https://docs.ton.org/v3/guidelines/dapps/cookbook#how-to-convert-between-user-friendly-and-raw-addresses-assemble-and-extract-them-from-strings
   * @private
   * @param {string} address 地址
   * @returns {string} 原始格式地址
   * @memberof TonConnectService
   */
  private _getRawAddress(address: string): string {
    return Address.parse(address).toRawString();
  }

  /**
   * 取得 使用者友善(User-friendly) 地址
   *
   * @description 安全的 base64/base64url 編碼包裝器，用於封裝原始地址
   * @see https://docs.ton.org/v3/documentation/smart-contracts/addresses/address-formats
   * @private
   * @param {string} address 地址
   * @param {CHAIN} chain 鏈別
   * @returns {string} 使用者友善地址
   * @memberof TonConnectService
   */
  private _getFriendlyAddress(address: string, chain: CHAIN): string {
    return Address.parse(address).toString({ urlSafe: true, bounceable: false, testOnly: chain === CHAIN.TESTNET });
  }

  /**
   * 取得 所有的公開地址 [Raw Address, User-friendly Address]
   *
   * @param { Account | null | undefined} account tonConnectUI 的 Address 物件
   * @returns {string[]} 所有的公開地址 [Raw Address, User-friendly Address]
   * @memberof TonConnectService
   */
  private _getPublicAddresses(account: Account | null | undefined): string[] {
    if (!account) return Array<string>(0);

    const { address, chain } = account!;
    if (!address || !chain) return Array<string>(0);

    return [this._getRawAddress(address), this._getFriendlyAddress(address, chain)];
  }

  private _checkWalletConnected(): void {
    if (!this._tonConnectUI.connected) {
      throw new Error('TonConnect is not connected.');
    }
  }

  /** 設定錢包狀態事件監聽 */
  private _registerRequirementListeners() {
    // 設定錢包 theme 事件監聽
    this.appService.themeSwitch$
      .pipe(
        untilDestroyed(this),
        map(theme => theme === 'sun'),
        distinctUntilChanged(),
      )
      .subscribe(isLight => {
        this._tonConnectUI.uiOptions = { uiPreferences: { theme: isLight ? THEME.LIGHT : THEME.DARK } };
      });

    /**
     * 設定錢包狀態事件監聽
     *
     * @see https://ton-connect.github.io/sdk/modules/_tonconnect_ui.html#subscribe-to-the-modal-window-state-changes
     */
    this._walletStatusListenerUnsubscribe = this._tonConnectUI.onStatusChange(walletAndWalletInfo => {
      if (walletAndWalletInfo === null) {
        this._accounts$.next([]);
        this._currentChainId$.next(null);
      } else {
        const { account } = walletAndWalletInfo;
        this._accounts$.next(this._getPublicAddresses(account));
        this._currentChainId$.next(account.chain);
      }
    });
  }

  /**
   * 依 contractAddress 獲取 Jetton 子錢包地址
   *
   * @private
   * @param {CHAIN} chain 鏈別
   * @param {string} contractAddress 智能合約 jetton 地址
   * @returns {Promise<Address>} Jetton 子錢包 Address 物件
   * @memberof TonConnectService
   */
  private async _getJettonWalletAddress(chain: CHAIN, contractAddress: string): Promise<Address> {
    this._checkWalletConnected();

    const cacheKey = `${this._tonConnectUI.account!.address}-${contractAddress}`;
    if (this._jettonWalletAddressCache.has(cacheKey)) {
      return this._jettonWalletAddressCache.get(cacheKey)!;
    }

    const tonClient = await this._getTonClient(chain);

    // 解析（驗證）Jetton 主合約地址
    const jettonMaster = Address.parse(contractAddress);

    // 解析（驗證）當前用戶的錢包地址
    const address = this._tonConnectUI.account!.address;
    const ownerAddress = Address.parse(address);
    const ownerAddressCell = beginCell().storeAddress(ownerAddress).endCell();

    // 在 Jetton 主合約上呼叫 `get_wallet_address` 方法來找到用戶的 Jetton 子錢包地址
    const result = await tonClient.runMethod(jettonMaster, 'get_wallet_address', [
      { type: 'slice', cell: ownerAddressCell },
    ]);

    const resultAddress = result.stack.readAddress();
    this._jettonWalletAddressCache.set(cacheKey, resultAddress);
    return resultAddress;
  }

  async checkExistingConnection(): Promise<boolean> {
    // wait TonConnect (has to) set up SSE connection with bridge
    // @see https://ton-connect.github.io/sdk/modules/_tonconnect_ui.html#detect-end-of-the-connection-restoring-process
    await this._tonConnectUI.connectionRestored;

    const isLogin = Boolean(this.localStorageService.loginToken);
    const { connected, account } = this._tonConnectUI;
    if (!isLogin || !connected || account === null) {
      this.updateConnectedStatus(false);
      return false;
    }

    try {
      // Reload 後，判斷已連線的 web3 錢包是否為綁定於DB的 web3 錢包
      const web3WalletRes = await firstValueFrom(this.accountApi.getWeb3Wallet());
      const connectedWallets = web3WalletRes?.data || [];
      const isExistingWallet = connectedWallets.some(
        wallet => wallet.address.toLowerCase() === this._getRawAddress(account.address).toLowerCase(),
      );
      this.updateConnectedStatus(isExistingWallet);
      return isExistingWallet;
    } catch (err) {
      console.error('Error checking existing connection in Ton Connect Service.', err);
      return false;
    }
  }

  async connectWallet(): Promise<void> {
    // 考慮到錢包連線後，若沒有簽章的情況下，無法切換錢包，故如果已經連線，則會直接斷線重新請求
    if (this._tonConnectUI.connected) {
      await this.disconnectWallet();
    }

    return new Promise<void>((resolve, reject) => {
      // @see https://ton-connect.github.io/sdk/modules/_tonconnect_ui.html#open-specific-wallet
      const modalListenerUnsubscribe = this._tonConnectUI.onModalStateChange((state: WalletsModalState) => {
        const { status, closeReason } = state;
        if (status === 'opened') {
          return;
        }

        if (closeReason === 'wallet-selected') {
          const address = this._tonConnectUI.account?.address;
          const chainId = this._tonConnectUI.account?.chain;

          if (address === undefined || chainId === undefined) {
            this.updateConnectedStatus(false);
            modalListenerUnsubscribe();
            reject("Wallet selected, but can't get address and chain ID.");
            return;
          }
        }

        modalListenerUnsubscribe();
        resolve();
      });

      this._tonConnectUI.openModal().catch(error => reject(error));
    });
  }

  async disconnectWallet(): Promise<void> {
    if (!this._tonConnectUI.connected) return;

    await this._tonConnectUI.disconnect();

    // 重置狀態
    this._isConnected$.next(false);
    this._accounts$.next([]);
    this._currentChainId$.next(null);
  }

  updateConnectedStatus(isConnected: boolean): void {
    if (!isConnected) {
      this.disconnectWallet();
    }

    this._isConnected$.next(isConnected);
  }

  async signMessage(message: string): Promise<Web3WalletSignatureInfo | null> {
    this._checkWalletConnected();

    const textData: SignDataPayload = {
      type: 'text',
      text: message,
      network: this._tonConnectUI.account!.chain,
      from: this._getRawAddress(this._tonConnectUI.account!.address),
    };
    const result = await this._tonConnectUI.signData(textData);
    const address = this._tonConnectUI.account!.address;
    const chian = result.payload?.network || this._tonConnectUI.account!.chain;
    const signatureInfo: Web3WalletSignatureInfo = {
      walletType: Web3WalletType.TonConnect,
      network: chian,
      address: this._getRawAddress(address),
      friendlyAddress: this._getFriendlyAddress(address, chian),
      signature: result.signature,
      publicKey: this._tonConnectUI.account?.publicKey,
      domain: result.domain,
      timestamp: result.timestamp,
    };
    return signatureInfo;
  }

  async fetchCurrentChainId(): Promise<number | string | null> {
    const chainId = this._tonConnectUI.account?.chain;
    return chainId === undefined ? null : chainId;
  }

  private _retryWithDelay<T>(
    fn: () => Promise<T>,
    retries: number = 3,
    delays: number[] = [1000, 2000, 2000],
  ): Observable<T> {
    return defer(() => from(fn())).pipe(
      retry({
        count: retries,
        delay: (_, retryCount) => timer(delays[retryCount - 1] ?? delays[delays.length - 1]),
      }),
    );
  }

  /**獲取主鏈 TON 餘額 */
  async fetchBalance(): Promise<string | null> {
    this._checkWalletConnected();

    const fetchBalanceOnce = async () => {
      const tonClient = await this._getTonClient(this._tonConnectUI.account!.chain);

      // 解析（驗證）錢包地址
      const address = this._tonConnectUI.account!.address;
      const parseAddress = Address.parse(address);

      // 獲取錢包 NanoTon（單位：n）餘額
      const balanceNanoTon = await tonClient.getBalance(parseAddress);

      // 將 NanoTon 轉為 TON（單位：TON）
      const balanceTon = fromNano(balanceNanoTon);
      return balanceTon;
    };

    try {
      return await lastValueFrom(this._retryWithDelay(() => fetchBalanceOnce()));
    } catch (error) {
      console.error('Error fetching balances in Ton Connect Service.', error);
      return null;
    }
  }

  /**獲取指定代幣 (Jetton) 的餘額 */
  async fetchContractBalance(network: NetworksInterface): Promise<string | null> {
    this._checkWalletConnected();

    const fetchBalanceOnce = async () => {
      const { contractAddress, chainId, decimals } = network;

      // 依照傳入的 chainId，確認 tonClient 的鏈別（主網/測試網）
      const walletChain = chainId === CHAIN.MAINNET ? CHAIN.MAINNET : CHAIN.TESTNET;
      const tonClient = await this._getTonClient(walletChain);

      // 從用戶的 Jetton 子錢包中獲取餘額（呼叫 `get_wallet_data` 方法）
      const userJettonWalletAddress = await this._getJettonWalletAddress(walletChain, contractAddress);
      const walletData = await tonClient.runMethod(userJettonWalletAddress, 'get_wallet_data');

      // 解析回傳的堆疊 (stack)
      const balance = walletData.stack.readBigNumber(); // 餘額 (未經處理的原始值)
      // const owner = walletData.stack.readAddress(); // 持有者地址
      // const jettonMasterAddress = walletData.stack.readAddress(); // Jetton 主合約地址
      // const jettonWalletCode = walletData.stack.readCell(); // 子錢包程式碼

      // 根據代幣定義的小數位數 (decimals) 來處理餘額，轉換為可讀格式
      const intBalance = balance / BigInt(10 ** decimals);
      const remainder = balance % BigInt(10 ** decimals);
      const finalBalance = intBalance.toString() + '.' + remainder.toString().padStart(decimals, '0');
      return String(parseFloat(finalBalance));
    };

    try {
      return await lastValueFrom(this._retryWithDelay(() => fetchBalanceOnce()));
    } catch (error) {
      // 如果用戶從未與此 Jetton 互動，他可能沒有對應的 Jetton 錢包，這會導致錯誤。
      console.error(
        'Error fetching Jetton balance (user might not have a Jetton wallet yet) in Ton Connect Service.',
        error,
      );
      return null;
    }
  }

  private async _sendTransaction(transaction: SendTransactionRequest): Promise<string | null> {
    this._checkWalletConnected();

    try {
      // 發送交易到錢包讓用戶簽名
      const result = await this._tonConnectUI.sendTransaction(transaction);

      // 將 BoC 轉換為 Cell
      const cell = Cell.fromBase64(result.boc);

      // 直接回傳 外部消息 (External Message) hash
      // **重要提示**: 這個 hash 只是消息的 hash，不代表交易在鏈上成功。
      return cell.hash().toString('hex');
    } catch (err) {
      this.toastService.show({
        title: 'Deposit request failed',
        message: 'You rejected the transaction request.',
        type: 'fail',
      });
      console.error('Error transaction failed in Ton Connect Service.', err);
      return null;
    }
  }

  async sendMainTransaction(toAddress: string, amount: string): Promise<string | null> {
    this._checkWalletConnected();

    const walletChain = this._tonConnectUI.account!.chain;
    const senderTonAddress = this._getRawAddress(this._tonConnectUI.account!.address);
    const recipientAddress = this._getFriendlyAddress(toAddress, walletChain);

    // 構建交易請求
    const transaction: SendTransactionRequest = {
      validUntil: this._transactionValidUntil,
      messages: [{ address: recipientAddress, amount: toNano(amount).toString() }],
      network: walletChain,
      from: senderTonAddress,
    };
    return this._sendTransaction(transaction);
  }

  async sendContractTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    this._checkWalletConnected();

    const { chainId, contractAddress } = network;
    const walletChain = chainId === CHAIN.MAINNET ? CHAIN.MAINNET : CHAIN.TESTNET;

    const senderTonAddress = this._tonConnectUI.account!.address;
    const recipientAddress = toAddress;
    const senderJettonWalletAddress = await this._getJettonWalletAddress(walletChain, contractAddress);

    // 將用戶輸入的 Jetton 數量 (例如 '1.23') 轉換為合約內部使用的大整數格式 (乘以 10 的 decimals 次方)
    const jettonAmount = BigInt(parseFloat(amount) * 10 ** network.decimals);

    /**
     * Jetton 轉帳標準的 op-code (操作碼)
     *
     * @see https://github.com/ton-blockchain/TEPs/blob/master/text/0074-jettons-standard.md#tl-b-schema
     * @description
     * |	Op-code			|	名稱									|	簡要說明												|	主要發送者						|
     * |--------------|-----------------------|--------------------------------|---------------------|
     * |	0x0f8a7ea5	|	transfer							|	用戶發起的轉帳指令，啟動整個流程		|	用戶主錢包						|
     * |	0x178d4519	|	internal_transfer			|	合約間的實際轉帳，轉移代幣所有權		|	付款方 Jetton 子錢包	|
     * |	0x7362d09c	|	transfer_notification	|	給收款人的到帳通知，用於更新錢包UI	|	收款方 Jetton 子錢包	|
     * |	0x595f07bc	|	burn									|	用戶發起的銷毀指令								|	用戶主錢包						|
     * |	0x7bdd97de	|	burn_notification			|	給銷毀發起人的完成回條						|	用戶的 Jetton 子錢包	|
     * |	0xd53276db	|	excesses							|	退還多餘的 Gas Fee，處理退款			|	任何 Jetton 子錢包		|
     */
    const JETTON_TRANSFER_OP_CODE = 0x0f8a7ea5;

    /**
     * forward_ton_amount 轉帳通知的費用（最少 0.000000001 TON）
     *
     * @see https://docs.ton.org/v3/guidelines/dapps/asset-processing/jettons#message-0
     * @description 必須大於 0 才能發送帶有 forward payload 的轉帳通知訊息。它是 value 的一部分 amount，並且必須小於 amount。
     */
    const DEFAULT_JETTON_TRANSFER_FORWARD_GAS_TON = 0.000000001;

    /** 支付給合約去執行操作所需的 Gas Fee */
    const DEFAULT_JETTON_TRANSFER_GAS_TON = 0.05;

    /**  */
    const bodyCell = beginCell()
      .storeUint(JETTON_TRANSFER_OP_CODE, 32) // op-code
      .storeUint(0, 64) // query_id (通常設為 0)
      .storeCoins(jettonAmount) // 要轉帳的 Jetton 數量 (需要轉換為 Nano 單位)
      .storeAddress(Address.parse(recipientAddress)) // 接收者地址
      .storeAddress(Address.parse(senderTonAddress)) // 交易發起者，用於退款
      .storeMaybeRef(null) // custom_payload (可選)
      .storeCoins(toNano(DEFAULT_JETTON_TRANSFER_FORWARD_GAS_TON)) // forward_ton_amount (轉給接收者合約的 TON，避免其 gas 不足)
      .storeMaybeRef(null) // forward_payload
      .endCell();

    // 構建交易請求
    const transaction: SendTransactionRequest = {
      validUntil: this._transactionValidUntil,
      messages: [
        {
          // 交易的目標地址是【付款者自己的 Jetton 子錢包地址】。 (我們是在命令這個地址上的合約去執行轉帳)
          address: senderJettonWalletAddress.toString(),

          // 支付給合約去執行操作所需的 Gas Fee。 (0.05 TON 通常足夠)
          amount: toNano(DEFAULT_JETTON_TRANSFER_GAS_TON).toString(),

          // 要執行的指令
          payload: bodyCell.toBoc().toString('base64'),
        },
      ],
      network: walletChain,
      from: this._getRawAddress(senderTonAddress),
    };
    return this._sendTransaction(transaction);
  }

  async switchNetwork(chainId: number | string | null): Promise<void> {
    const networkToSwitch = Object.values(CHAIN).find(network => network === chainId);

    if (!networkToSwitch) {
      this.toastService.show({
        title: 'Network not detected',
        message:
          'This network is not enabled in your wallet. Please enable it and try again or select a different network.',
        type: 'fail',
      });

      throw new Error('Failed to switch network: network not found');
    }
  }
}
